/*
 * VC: sold-out capture panel (all products).
 *
 * Modes (set via data-mode on the element, driven by the 'made-to-order' product tag):
 *   restock  – Klaviyo back-in-stock subscription + event.
 *   bespoke  – commission enquiry; tracked as a Klaviyo event only.
 *
 * The panel updates itself on the client for every variant: it shows for
 * sold-out variants (with that variant's text + id) and hides for in-stock
 * ones, independent of Shopify's partial re-render.
 */
(function () {
  if (window.customElements && customElements.get('vc-restock-form')) return;

  /* Built via RegExp constructor with no backslash escapes so the pattern
     survives every templating layer intact. */
  var EMAIL_RE = new RegExp('^[^@ ]+@[^@ ]+[.][^@ ]+$');

  class VCRestockForm extends HTMLElement {
    connectedCallback() {
      this.eyebrow = this.querySelector('.vc-restock__eyebrow');
      this.form = this.querySelector('.vc-restock__form');
      this.input = this.querySelector('.vc-restock__input');
      this.button = this.querySelector('.vc-restock__button');
      this.note = this.querySelector('.vc-restock__note');
      this.intro = this.querySelector('.vc-restock__intro');
      this.message = this.querySelector('.vc-restock__message');
      try { this.variants = JSON.parse(this.getAttribute('data-variants') || '{}'); } catch (e) { this.variants = {}; }
      this.initialId = this.dataset.initialId || '';
      this.mode = this.dataset.mode === 'bespoke' ? 'bespoke' : 'restock';
      this.single = this.dataset.single === 'true';
      this.productTitle = this.dataset.productTitle || '';
      this.buyBlock = this.closest('.buy-buttons-block');
      if (!this.buttonLabel) this.buttonLabel = this.button ? this.button.textContent.trim() : 'Notify me';

      if (this.form && !this._bound) {
        this._bound = true;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      this._sync = this.sync.bind(this);
      this._deferredSync = function () { setTimeout(this._sync, 80); }.bind(this);
      window.addEventListener('popstate', this._sync);
      document.addEventListener('click', this._deferredSync, true);
      document.addEventListener('change', this._deferredSync, true);
      this._poll = setInterval(this._sync, 250);
      this.sync();
    }

    disconnectedCallback() {
      if (this._poll) clearInterval(this._poll);
      window.removeEventListener('popstate', this._sync);
      document.removeEventListener('click', this._deferredSync, true);
      document.removeEventListener('change', this._deferredSync, true);
    }

    currentId() {
      var root = this.buyBlock || document;
      var idInput = root.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
      if (idInput && idInput.value && this.variants[idInput.value]) return idInput.value;
      var checked = document.querySelector('input[type="radio"][data-variant-id]:checked');
      if (checked && checked.dataset.variantId && this.variants[checked.dataset.variantId]) return checked.dataset.variantId;
      try {
        var u = new URL(window.location.href).searchParams.get('variant');
        if (u && this.variants[u]) return u;
      } catch (e) {}
      return this.initialId;
    }

    /* Human label for the current variant: "Size 38", or the product name for
       single-variant products (avoids "Size Default Title"). */
    labelFor(v) {
      if (this.single || !v.t || v.t === 'Default Title') return this.productTitle;
      return 'Size ' + v.t;
    }

    sync() {
      var id = this.currentId();
      var v = this.variants[id];
      if (!v) return;
      var soldOut = v.a === false;
      if (this.buyBlock) this.buyBlock.classList.toggle('buy-buttons-block--vc-restock', soldOut);
      if (!soldOut) { this.hidden = true; return; }
      this.hidden = false;
      if (this._renderedId === id) return;
      this._renderedId = id;

      var label = this.labelFor(v);
      this.currentSize = v.t;
      this.dataset.variantId = id;
      this.dataset.variantTitle = v.t;

      if (this.mode === 'restock') {
        if (this.eyebrow) this.eyebrow.textContent = label + ' — sold out';
        if (this.note) { this.note.textContent = 'Be first in line when ' + label + ' returns.'; this.note.hidden = false; }
      } else if (this.note) {
        this.note.hidden = false;
      }

      if (this.intro) this.intro.hidden = false;
      if (this.form) this.form.hidden = false;
      if (this.button) { this.button.disabled = false; this.button.textContent = this.buttonLabel; }
      if (this.message) { this.message.hidden = true; this.message.textContent = ''; this.message.removeAttribute('data-state'); }
      if (this.input) this.input.value = '';
    }

    setMessage(text, state) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.setAttribute('data-state', state);
      this.message.hidden = false;
    }

    async onSubmit(event) {
      event.preventDefault();
      var email = ((this.input && this.input.value) || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.setMessage('Please enter a valid email address.', 'error');
        if (this.input) this.input.focus();
        return;
      }

      var companyId = this.dataset.companyId;
      var variantId = this.dataset.variantId;
      var size = this.dataset.variantTitle || this.currentSize || '';
      var label = this.button ? this.button.textContent : '';
      if (this.button) { this.button.disabled = true; this.button.textContent = 'One moment…'; }

      var tracked = false;
      try { tracked = await this.trackEvent(companyId, variantId, size, email); } catch (e) { tracked = false; }

      var ok = tracked;
      if (this.mode === 'restock') {
        var sub = false;
        try { sub = await this.subscribeClient(companyId, variantId, email); } catch (e) { sub = false; }
        if (!sub) { try { sub = await this.subscribeLegacy(companyId, variantId, email); } catch (e) { sub = false; } }
        ok = sub || tracked;
      }

      if (ok) {
        if (this.form) this.form.hidden = true;
        if (this.note) this.note.hidden = true;
        if (this.intro) this.intro.hidden = true;
        if (this.mode === 'bespoke') {
          this.setMessage('Received. We’ll be in touch personally to discuss your commission.', 'success');
        } else {
          var v = this.variants[variantId] || { t: size };
          this.setMessage('You’re on the list. We’ll email you the moment ' + this.labelFor(v) + ' is back.', 'success');
        }
      } else {
        if (this.button) { this.button.disabled = false; this.button.textContent = label; }
        this.setMessage('Something went wrong. Please try again.', 'error');
      }
    }

    async trackEvent(companyId, variantId, size, email) {
      var metric = this.mode === 'bespoke' ? 'VC Commission Enquiry' : 'VC Restock Requested';
      var res = await fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: metric } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
              properties: {
                Product: this.productTitle,
                Size: size,
                'Variant ID': variantId,
                'Product URL': window.location.href.split('?')[0]
              }
            }
          }
        })
      });
      return res.status === 202 || res.ok;
    }

    async subscribeClient(companyId, variantId, email) {
      var res = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
            }
          }
        })
      });
      return res.status === 202 || res.ok;
    }

    async subscribeLegacy(companyId, variantId, email) {
      var body = new URLSearchParams();
      body.append('a', companyId);
      body.append('email', email);
      body.append('variant', variantId);
      body.append('platform', 'shopify');
      var res = await fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
        body: body.toString()
      });
      if (!res.ok) return false;
      try { var j = await res.json(); return !!(j && (j.success === true || j.data)); } catch (e) { return true; }
    }
  }

  customElements.define('vc-restock-form', VCRestockForm);
})();